import { useEffect, useState } from 'react'
import Yasqe from '@triply/yasqe'
import '../../node_modules/@triply/yasgui/build/yasgui.min.css'
import '../../node_modules/@triply/yasqe/build/yasqe.min.css'
import { store } from '@/store'
import { useLatest } from '@/hooks/use-latest'
import { CodeEditorHandle } from '@/redux/current-viewer'
import { useFloatingState } from '@/components/editor/panels/panel-registry'
import { useLocalSettings } from '@/components/editor/local-settings'

export function SparqlEditor(props: {
  value: string
  onBlur: (value: string) => void
  mode: 'update-value' | 'init-value'
  id: string
  /** Leaves out YASQE's own run and share buttons, for when the surrounding UI runs the query */
  hideButtons?: boolean
}) {
  const [yas, setYas] = useState<Yasqe | null>(null)
  const [ref, setRef] = useState<any>(null)
  const floatingState = useFloatingState()
  const localSettings = useLocalSettings()

  useEffect(function createYasqeAndPublishToRegistry() {
    if (!ref) return () => {}

    // Default resizer is broken with react-rnd <Rnd />
    const newYas = new Yasqe(ref, {
      resizeable: false,
      ...(props.hideButtons && {
        showQueryButton: false,
        // falsy leaves out the share button; the typings only allow a link factory
        createShareableLink: null as unknown as Yasqe["config"]["createShareableLink"],
      }),
    })
    // for safety, if not set the default query is any "previous" Yasqe query
    newYas.setValue("")
    setYas(newYas)
    const handle: CodeEditorHandle = {
      mode: 'sparql',
      getValue: () => newYas.getValue(),
      onSettled: handler => {
        newYas.on('blur', handler)
        return () => newYas.off('blur', handler)
      },
    }
    store.dispatch({type: 'ADD_CODE_EDITOR', data: {id: props.id, editor: handle}})
    return () => {
      newYas.destroy()
      store.dispatch({type: 'REMOVE_CODE_EDITOR', data: props.id})
    }
  }, [ref, props.id, props.hideButtons])

  const {onBlur, value} = props
  const onBlurRef = useLatest(onBlur)

  useEffect(() => {
    if (!yas) return
    yas.on('blur', () => onBlurRef.current(yas.getValue()))
  }, [yas, onBlurRef])

  useEffect(() => {
    if (!yas || props.mode === 'update-value') return
    yas.setValue(props.value)
  }, [yas])

  useEffect(() => {
    if (!yas || props.mode === 'init-value') return
    yas.setValue(props.value)
  }, [yas, props.value, props.mode])

  if (floatingState !== "docked") {
    yas?.expandEditor()
  }

  return (
    <>
      <style>{`
        .yasqe .CodeMirror {
          font-family: '${localSettings.fontFamily}', Monaco, Consolas, "Lucida Console", monospace;
          font-size: ${localSettings.fontSize || '14px'};
          font-weight: ${localSettings.fontWeight || '400'};
       
          resize: ${floatingState === "docked" ? "vertical" : "none"};
          min-height: ${floatingState !== "docked" ? "100%" : "auto"};
        }
        .CodeMirror pre.CodeMirror-line {
          line-height: 1.25;
        }
      `}</style>
      {floatingState !== "docked" && (
        <style>{`
          #${props.id}, .yasqe, .CodeMirror {
            flex-grow: 1;
            height: 100%;
            min-height: 100%;
          }        
        `}</style>
      )}
      <div ref={setRef} id={props.id} />
    </>
  )
}
